import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useSuperuserGuard } from "@/hooks/use-superuser-guard";
import { DeviceDetailsTable, DeviceTableRefType } from "@/components/devices/DeviceDetailsTable";
import { useRef, useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";

const DeviceDetails = () => {
  // This hook will redirect non-superusers away from this page
  useSuperuserGuard();
  
  // Create a ref to access the table's methods
  const tableRef = useRef<DeviceTableRefType>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Quick refresh - just update from database
  const refreshDeviceList = () => {
    if (tableRef.current) {
      tableRef.current.refetch().then(() => {
        toast({
          title: "Device list updated",
          description: "The device list has been refreshed from database"
        });
      });
    }
  };

  // Deep refresh - call API for each device
  const refreshAllDevicesFromApi = async () => {
    if (tableRef.current) {
      setIsRefreshing(true);
      
      try {
        await tableRef.current.refreshAllDevices();
      } catch (error) {
        toast({
          title: "Error refreshing devices",
          description: "Some devices failed to refresh. Check console for details.",
          variant: "destructive"
        });
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader
            {...PAGE_HEADERS.adminDeviceDetails}
            actions={
              <div className="flex gap-2">
                <Button 
                  onClick={refreshDeviceList} 
                  variant="outline" 
                  size="sm"
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Quick Refresh
                </Button>
                <Button 
                  onClick={refreshAllDevicesFromApi} 
                  variant="default" 
                  size="sm"
                  className="gap-2 bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
                  disabled={isRefreshing}
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Refreshing...' : 'Deep Refresh All'}
                </Button>
              </div>
            }
          />
          <div className="px-4 py-6 md:p-8 space-y-6 md:space-y-8" style={{ paddingTop: "8.5rem" }}>
            <DeviceDetailsTable ref={tableRef} />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default DeviceDetails;
